import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import type { Event, UploadedFile } from '../types'
import { LocationPicker, type LocationValue } from './maps/LocationPicker'
import { toNumericCoordinate } from '../utils/location'
import { SingleFileUploader } from './files/SingleFileUploader'

interface EventFormModalProps {
  event?: Event | null
  onClose: () => void
  onSave: (data: any) => Promise<void>
}

export default function EventFormModal({ event, onClose, onSave }: EventFormModalProps) {
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    status: 'upcoming' as Event['status'],
    visibility: 'public' as Event['visibility'],
    coverImage: '',
    coverImageFileId: '',
    tags: [] as string[],
    location: {
      address: '',
      lat: null as number | null,
      lng: null as number | null,
    } as LocationValue,
  })

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title,
        description: event.description,
        startDate: event.startDate ? new Date(event.startDate).toISOString().slice(0, 16) : '',
        endDate: event.endDate ? new Date(event.endDate).toISOString().slice(0, 16) : '',
        status: event.status,
        visibility: event.visibility,
        coverImage: event.coverImage || '',
        coverImageFileId: (event as any).coverImageFileId || '',
        tags: event.tags || [],
        location: {
          address: event.location?.address || event.location?.name || '',
          lat: toNumericCoordinate(event.location?.latitude || event.location?.lat),
          lng: toNumericCoordinate(event.location?.longitude || event.location?.lng),
        },
      })
    } else {
      resetForm()
    }
  }, [event])

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      startDate: '',
      endDate: '',
      status: 'upcoming',
      visibility: 'public',
      coverImage: '',
      coverImageFileId: '',
      tags: [],
      location: {
        address: '',
        lat: null,
        lng: null,
      },
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const dataToSave = {
        title: formData.title,
        description: formData.description,
        startDate: formData.startDate,
        endDate: formData.endDate || undefined,
        status: formData.status,
        visibility: formData.visibility,
        coverImage: formData.coverImage || undefined,
        coverImageFileId: formData.coverImageFileId || undefined,
        tags: formData.tags,
        location: (() => {
          const { address, lat, lng } = formData.location
          const hasLatLng = typeof lat === 'number' && typeof lng === 'number'
          if (!hasLatLng && !address) {
            return undefined
          }
          return {
            address: address || undefined,
            lat: hasLatLng ? lat : undefined,
            lng: hasLatLng ? lng : undefined,
          }
        })(),
      }

      await onSave(dataToSave)
    } catch (error) {
      console.error('Failed to save event:', error)
    } finally {
      setLoading(false)
    }
  }

  const coverImageValue: UploadedFile | null = formData.coverImage
    ? {
        id: formData.coverImageFileId || 'event-cover',
        url: formData.coverImage,
        thumbnailUrl: formData.coverImage,
        fileType: 'image',
        size: 0,
        filename: formData.coverImage.split('/').pop() || 'event-cover',
        originalFilename: formData.coverImage.split('/').pop() || undefined,
      }
    : null

  const addTag = (tag: string) => {
    if (tag.trim() && !formData.tags.includes(tag.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tag.trim()]
      }))
    }
  }

  const removeTag = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }))
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-900/50" onClick={onClose} />

        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              {event ? 'Edit Event' : 'Create Event'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                className="input w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                className="input w-full min-h-[120px]"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date *
                </label>
                <input
                  type="datetime-local"
                  value={formData.startDate}
                  onChange={(e) => setFormData((prev) => ({ ...prev, startDate: e.target.value }))}
                  className="input w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="datetime-local"
                  value={formData.endDate}
                  onChange={(e) => setFormData((prev) => ({ ...prev, endDate: e.target.value }))}
                  className="input w-full"
                  min={formData.startDate}
                />
              </div>
            </div>

            <div>
              <LocationPicker
                label="Location"
                description="Search for the event location or drop a pin on the map."
                value={formData.location}
                onChange={(location: LocationValue) => setFormData((prev) => ({ ...prev, location }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value as Event['status'] }))}
                  className="input w-full"
                >
                  <option value="upcoming">Upcoming</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visibility
                </label>
                <select
                  value={formData.visibility}
                  onChange={(e) => setFormData((prev) => ({ ...prev, visibility: e.target.value as Event['visibility'] }))}
                  className="input w-full"
                >
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>
            </div>

            <SingleFileUploader
              label="Cover Image"
              helperText="Upload a banner image for the event."
              value={coverImageValue}
              onChange={(file) => {
                setFormData((prev) => ({
                  ...prev,
                  coverImage: file?.url || '',
                  coverImageFileId: file?.id || '',
                }))
              }}
              privacy="public"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(index)}
                        className="ml-1 text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  className="input w-full"
                  placeholder="Add a tag and press Enter"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      const value = (e.target as HTMLInputElement).value.trim()
                      if (value) {
                        addTag(value)
                        ;(e.target as HTMLInputElement).value = ''
                      }
                    }
                  }}
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary flex-1"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary flex-1"
                disabled={loading}
              >
                {loading ? 'Saving...' : event ? 'Update Event' : 'Create Event'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
